import Enumerable from "linq";
import store from "../../../../store";
import * as Moment from "moment";
import { IConfiguration } from "../../../../domain/configuration";
import { IWebApplicationMessageCommunicatable, WebApplicationExecutePatterns, WebApplicationInformation } from "../../../../domain/localhost";
import { ExecuteRequest, ExternalScriptExecuteResult, ScriptLinkItem } from "../../../../domain/localhost/items";
import { CommunicatorBase } from "./CommunicatorBase";
import { Sex } from "../../../../domain/patient";
import { SessionStorageAccessor } from "../../../../store/SessionStorageAccessor";
import { BrowserKinds, BrowserUtility } from "../../../../utilities";

interface IPostedData {
  data: string;
}

interface IReferralInformation {
  referralId: string;
  medicalInstitutionInformation: IMedicalInstitutionInformation;
  documentConsent: string;
}

interface IMedicalInstitutionInformation {
  medicalInstitutionCode: string;
  name: string;
  postalCode: string;
  address: string;
  telephoneNumber: string[];
  faxNumber: string[];
  tenantId: string;
  electronicMedicalRecordSharingSupport: boolean;
  studyImageSharingSupport: boolean;
  department: string;
}

interface IExecuteParameter {
  patientInformation: IPatientInformation;
  userInformation: IUserInformation;
  redirectURL: string;
  departmentInformation: IDepartmentInformation;
}

interface IPatientInformation {
  patientNumber: string;
  kanjiName?: string;
  kanaName?: string;
  sex?: string;
  birthDate?: string;
  address?: string;
}

interface IUserInformation {
  userId: string;
  // userName: string;
}

interface IDepartmentInformation {
  code: string;
  name: string;
}

class ValueInformation {
  constructor(public objectName: string, public value: string) {
  }
}

interface IIntroductionSelectorConfiguration {
  uri: string;
  defaultObjectNamesMapping: {
    referralId: string[];
    medicalInstitutionCode: string[];
    name: string[];
    postalCode: string[];
    address: string[];
    telephoneNumber: string[];
    faxNumber: string[];
    tenantId: string[];
    electronicMedicalRecordSharingSupport: string[];
    studyImageSharingSupport: string[];
    department: string[];
    documentConsent: string[];
  };
}

export class IntroductionSelectorCommunicator extends CommunicatorBase implements IWebApplicationMessageCommunicatable {
  constructor(request: ExecuteRequest, registerResult: (result: ExternalScriptExecuteResult) =>  Promise<void>, getConfiguration: (key: string) => Promise<any>, private _requestKey: string) {
    super(request, registerResult, getConfiguration);
  }
  private _configuration: IIntroductionSelectorConfiguration;
  private readonly _analyzePostedDataCommandText = "analyzePostedData";
  
  public isProcessible(): boolean {
    if (!this._request || !this._request.exePath) { return false; }
    const configuration = (store.state.configuration as IConfiguration).behavior.localhost.externalScript.webApplication.introductionSelector;
    return configuration.isEnable && (new RegExp(configuration.pathExpression, "i")).test(this._request.exePath);
  }

  async initialize(): Promise<void> {
    const configuration = await this._getConfiguration("IntroductionSelector");
    console.debug(configuration);
    if (!configuration || !configuration.uri) { throw new Error("設定の取得に失敗しました。"); }
    this._configuration = configuration;
  }

  async getApplicationInformation(communicatorMessage: string): Promise<WebApplicationInformation> {
    if (!communicatorMessage) {
      SessionStorageAccessor.postedData = null;
      const parameter = await this.createParameterData();
      const url = this._configuration.uri + "?data=" + encodeURIComponent(this.toBase64Text(parameter));
      return new WebApplicationInformation(url, WebApplicationExecutePatterns.Get, null, null);
    }
    if (communicatorMessage === this._analyzePostedDataCommandText) {
      // postedDataはPostReceiver.aspxで格納している
      const postedData = SessionStorageAccessor.postedData;
      SessionStorageAccessor.postedData = null;
      return new WebApplicationInformation(null, WebApplicationExecutePatterns.AnalyzePostedData, null, postedData);
    }
    throw new Error(communicatorMessage + "に対応する処理がありません。");
  }

  private toBase64Text(parameter: IExecuteParameter): string {
    if (!parameter) { return ""; }
    if (BrowserUtility.getBrowserKind() === BrowserKinds.InternetExplorer) {
      // IEではTextEncoderが使えない。（Unit8Arrayのreduceでもエラーが発生する）
      // 他にもbase64変換する方法はあるようだが、そもそもWEBアプリ側が対応していないので、IEは非対応としている。
      throw new Error("IEは非対応です。");
    }
    const textEncoder = new TextEncoder();
    const uint8Array = textEncoder.encode(JSON.stringify(parameter));
    return btoa(uint8Array.reduce((binaryString, uint8) => binaryString + String.fromCharCode(uint8), ""));
  }

  private async createParameterData(): Promise<IExecuteParameter> {
    const patient = await this.getPatient(this._request.patientId);
    const baseUrl = location.protocol + "//" + location.host;
    return {
      patientInformation: {
        patientNumber: patient.id,
        kanjiName: patient.name,
        kanaName: patient.kanaName,
        sex: this.toPatientSecCode(patient.sex),
        birthDate: this.toBirthDateText(patient.birthday),
        address: null,  // TODO: 必要？
      },
      userInformation: {
        userId: store.getters.user.id,  // TODO: 外部処理パラメータ経由で取得したほうがよい？
      },
      redirectURL: baseUrl + "/PostReceiver?redirect=" + encodeURIComponent(baseUrl + "/WebApplicationAdapter?requestKey=" + this._requestKey + "&communicatorMessage=" + this._analyzePostedDataCommandText),
      departmentInformation: !!this._request.department
        ? {
          code: this._request.department.id,
          name: this._request.department.name,
        }
        : null,
    };
  }

  analyzeMessage(message: any, postedData: IPostedData): void {
    if (!!message && !postedData) {
      // dev-serverで動作させているとイベントが飛んでくるので除外している
      return;
    }
    const result = this.createExecuteResult(postedData);
    this._registerResult(result)
    .then(() => {
      this.onComplete();
    });
  }

  private toBirthDateText(date: Date): string {
    if (!date || Number.isNaN(date.getTime())) { return null; }
    return Moment(date).format("YYYYMMDD");
  }

  private toPatientSecCode(sex: Sex): string {
    if (sex == null) { return null; }
    switch (sex) {
      case Sex.Male: return "M";
      case Sex.Female: return "F";
      case Sex.Unknown: return "O";
    }
  }

  private createExecuteResult(postedData: IPostedData): ExternalScriptExecuteResult {
    if (!postedData || postedData.data == null) {
      const emptyResult = new ExternalScriptExecuteResult();
      emptyResult.isComplete = true;
      emptyResult.isCancel = true;
      emptyResult.values = [];
      emptyResult.scriptLinkResults = [];
      return emptyResult;
    }
    // TODO: オブジェクトの引数で指定もできたほうがよい？
    const source = this.toSelectedInformationFromBase64Text(postedData.data);
    console.debug(source);
    const mapping = this._configuration.defaultObjectNamesMapping;
    const values = Enumerable.from([])
    .concat(this.createValues(source.referralId, mapping.referralId))
    .concat(this.createValues(source.medicalInstitutionInformation.medicalInstitutionCode, mapping.medicalInstitutionCode))
    .concat(this.createValues(source.medicalInstitutionInformation.name, mapping.name))
    .concat(this.createValues(source.medicalInstitutionInformation.postalCode, mapping.postalCode))
    .concat(this.createValues(source.medicalInstitutionInformation.address, mapping.address))
    .concat(this.createValuesFromArray(source.medicalInstitutionInformation.telephoneNumber, mapping.telephoneNumber))
    .concat(this.createValuesFromArray(source.medicalInstitutionInformation.faxNumber, mapping.faxNumber))
    .concat(this.createValues(source.medicalInstitutionInformation.tenantId, mapping.tenantId))
    .concat(this.createValuesFromBoolean(source.medicalInstitutionInformation.electronicMedicalRecordSharingSupport, mapping.electronicMedicalRecordSharingSupport))
    .concat(this.createValuesFromBoolean(source.medicalInstitutionInformation.studyImageSharingSupport, mapping.studyImageSharingSupport))
    .concat(this.createValues(source.medicalInstitutionInformation.department, mapping.department))
    .concat(this.createValues(source.documentConsent, mapping.documentConsent))
    .toArray();

    const result = new ExternalScriptExecuteResult();
    result.isComplete = true;
    result.values = [];
    result.scriptLinkResults = this.toScriptLinkResults(this._request.scriptLinkItems, values);
    return result;
  }

  private toSelectedInformationFromBase64Text(data: string): IReferralInformation {
    console.debug("toSelectedInformationFromBase64Text()");
    console.debug(data);
    const receivedText = decodeURIComponent(escape(atob(data)));  // PostReceiverでの変換を戻す
    console.debug(receivedText);
    return JSON.parse(receivedText);
  }

  private createValues(value: string, objectNames: string[]): ValueInformation[] {
    if (!objectNames || objectNames.length === 0) { return []; }
    return Enumerable.from(this._request.scriptLinkItems)
      .where(i => !!i.name && objectNames.indexOf(i.name) >= 0)
      .select(i => new ValueInformation(i.name, value))
      .toArray();
  }

  createValuesFromArray(values: string[], objectNames: string[]): any {
    // TODO: 先頭だけでよい？
    const text = values && values.length > 0 ? values[0] : "";
    return this.createValues(text, objectNames);
  }

  createValuesFromBoolean(value: boolean, objectNames: string[]): any {
    // TODO: 1 or 0 でよい？
    const text = value ? "1" : "0";
    return this.createValues(text, objectNames);
  }

  private toScriptLinkResults(scriptLinkItems: ScriptLinkItem[], values: ValueInformation[]): ScriptLinkItem[] {
    if (!values) { return []; }
    return Enumerable.from(scriptLinkItems)
      .select(i => this.createResultItem(i, Enumerable.from(values).firstOrDefault(v => v.objectName === i.name)))
      .where(i => !!i)
      .toArray();
  }

  private createResultItem(source: ScriptLinkItem, value: ValueInformation): ScriptLinkItem {
    if (!value) { return null; }
    const cloned = source.clone();
    cloned.value = value.value ? value.value : "";
    return cloned;
  }
}
